import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, BookOpen, Download, RefreshCw, Loader2, ClipboardList } from 'lucide-react';
import { api } from '@/services';
import { useApp } from '@/context/AppContext';

interface UserEntry {
  id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  student_id: string | null;
  role: string;
  status: string;
  last_login: string | null;
  created_at: string;
}

interface QuizScoreEntry {
  user_id: number;
  student_id: string | null;
  first_name: string | null;
  last_name: string | null;
  experiment_number: number;
  experiment_name: string;
  total_questions: number;
  questions_correct: number;
  score_percentage: string;
  best_score_percentage: string;
  attempts: number;
  completed_at: string;
}

interface ExperimentStat {
  experiment_number: number;
  experiment_name: string;
  students_started: string;
  students_completed: string;
}

interface CourseStats {
  courseId: string;
  totalStudents: number;
  experimentStats: ExperimentStat[];
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { state } = useApp();
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [stats, setStats] = useState<Record<string, CourseStats>>({});
  const [loading, setLoading] = useState(true);
  const [quizScores, setQuizScores] = useState<Record<string, QuizScoreEntry[]>>({});
  const [activeTab, setActiveTab] = useState<'users' | 'chem2131' | 'chem1131' | 'quizScores'>('users');
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, stats2131Res, stats1131Res] = await Promise.all([
        api.get<{ success: boolean; users: UserEntry[] }>('/admin/users'),
        api.get<{ success: boolean } & CourseStats>('/admin/courses/chem2131/stats'),
        api.get<{ success: boolean } & CourseStats>('/admin/courses/chem1131/stats'),
      ]);

      if (usersRes.data?.users) setUsers(usersRes.data.users);
      if (stats2131Res.data) setStats(prev => ({ ...prev, chem2131: stats2131Res.data as CourseStats }));
      if (stats1131Res.data) setStats(prev => ({ ...prev, chem1131: stats1131Res.data as CourseStats }));

      // Fetch quiz scores
      const [quiz2131Res, quiz1131Res] = await Promise.all([
        api.get<{ success: boolean; scores: QuizScoreEntry[] }>('/admin/courses/chem2131/quiz-scores'),
        api.get<{ success: boolean; scores: QuizScoreEntry[] }>('/admin/courses/chem1131/quiz-scores'),
      ]);
      if (quiz2131Res.data?.scores) setQuizScores(prev => ({ ...prev, chem2131: quiz2131Res.data!.scores }));
      if (quiz1131Res.data?.scores) setQuizScores(prev => ({ ...prev, chem1131: quiz1131Res.data!.scores }));
    } catch (e) {
      console.error('Failed to fetch admin data:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (state.authMode !== 'instructor') {
      navigate('/courses');
      return;
    }
    fetchData();
  }, []);

  const handleExport = async (courseId: string) => {
    setExporting(true);
    try {
      const res = await api.get<{ success: boolean; data: unknown[]; course: { code: string; name: string }; exportDate: string }>(
        `/admin/export/${courseId}`
      );
      if (res.data) {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${courseId}_export_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Export failed:', e);
    }
    setExporting(false);
  };

  const renderUsers = () => (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Student ID</th>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 ? (
            <tr><td colSpan={7} className="admin-empty">No users yet</td></tr>
          ) : users.map(u => (
            <tr key={u.id}>
              <td>{u.id}</td>
              <td>{u.student_id || '-'}</td>
              <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '-'}</td>
              <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
              <td>{u.status}</td>
              <td>{u.last_login ? new Date(u.last_login).toLocaleDateString() : '-'}</td>
              <td>{new Date(u.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderCourseStats = (courseId: string) => {
    const courseStats = stats[courseId];
    if (!courseStats) return <p className="admin-empty">No data available</p>;

    return (
      <div>
        <div className="admin-stat-cards">
          <div className="stat-card">
            <div className="stat-value">{courseStats.totalStudents}</div>
            <div className="stat-label">Students</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{courseStats.experimentStats?.length || 0}</div>
            <div className="stat-label">Experiments</div>
          </div>
          <div className="stat-card action" onClick={() => handleExport(courseId)}>
            {exporting ? <Loader2 size={20} className="spin" /> : <Download size={20} />}
            <div className="stat-label">Export Data</div>
          </div>
        </div>

        <h3 className="admin-section-title">Experiment Progress</h3>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Experiment</th>
                <th>Started</th>
                <th>Completed</th>
                <th>Completion Rate</th>
              </tr>
            </thead>
            <tbody>
              {courseStats.experimentStats?.map(stat => {
                const started = parseInt(stat.students_started) || 0;
                const completed = parseInt(stat.students_completed) || 0;
                const rate = started > 0 ? Math.round((completed / started) * 100) : 0;
                return (
                  <tr key={stat.experiment_number}>
                    <td>{stat.experiment_number}</td>
                    <td>{stat.experiment_name}</td>
                    <td>{started}</td>
                    <td>{completed}</td>
                    <td>
                      <div className="progress-bar-wrap">
                        <div className="progress-bar" style={{ width: `${rate}%` }} />
                        <span>{rate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuizScores = () => {
    const allScores = [...(quizScores.chem2131 || []), ...(quizScores.chem1131 || [])];
    if (allScores.length === 0) return <p className="admin-empty">No quiz scores yet</p>;

    return (
      <div>
        <div className="admin-stat-cards">
          <div className="stat-card">
            <div className="stat-value">{allScores.length}</div>
            <div className="stat-label">Total Submissions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {allScores.length > 0 ? Math.round(allScores.reduce((s, q) => s + parseFloat(q.best_score_percentage || '0'), 0) / allScores.length) : 0}%
            </div>
            <div className="stat-label">Avg Best Score</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{new Set(allScores.map(s => s.user_id)).size}</div>
            <div className="stat-label">Students</div>
          </div>
        </div>

        <h3 className="admin-section-title">Quiz Scores by Student</h3>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Student ID</th>
                <th>Name</th>
                <th>Experiment</th>
                <th>Score</th>
                <th>Best</th>
                <th>Attempts</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {allScores.map((s, i) => (
                <tr key={i}>
                  <td>{s.student_id || '-'}</td>
                  <td>{[s.first_name, s.last_name].filter(Boolean).join(' ') || '-'}</td>
                  <td>Exp {s.experiment_number}: {s.experiment_name}</td>
                  <td>
                    <span className={`score-badge ${parseFloat(s.score_percentage) >= 80 ? 'high' : parseFloat(s.score_percentage) >= 60 ? 'mid' : 'low'}`}>
                      {s.questions_correct}/{s.total_questions} ({parseFloat(s.score_percentage).toFixed(0)}%)
                    </span>
                  </td>
                  <td>{parseFloat(s.best_score_percentage).toFixed(0)}%</td>
                  <td>{s.attempts}</td>
                  <td>{s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Dashboard</h1>
        <button className="admin-refresh" onClick={fetchData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} /> Users ({users.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'chem2131' ? 'active' : ''}`}
          onClick={() => setActiveTab('chem2131')}
        >
          <BookOpen size={16} /> CHEM 2131
        </button>
        <button
          className={`admin-tab ${activeTab === 'chem1131' ? 'active' : ''}`}
          onClick={() => setActiveTab('chem1131')}
        >
          <BookOpen size={16} /> CHEM 1131
        </button>
        <button
          className={`admin-tab ${activeTab === 'quizScores' ? 'active' : ''}`}
          onClick={() => setActiveTab('quizScores')}
        >
          <ClipboardList size={16} /> Quiz Scores
        </button>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="admin-loading">
            <Loader2 size={32} className="spin" />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'chem2131' && renderCourseStats('chem2131')}
            {activeTab === 'chem1131' && renderCourseStats('chem1131')}
            {activeTab === 'quizScores' && renderQuizScores()}
          </>
        )}
      </div>

      <style>{`
        .admin-dashboard {
          color: var(--text-primary, #FFFFFF);
          padding: 24px 32px;
          max-width: 1200px;
        }
        .admin-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }
        .admin-header h1 {
          font-size: 24px;
          font-weight: 700;
          margin: 0;
          flex: 1;
        }
        .admin-refresh {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(99, 102, 241, 0.2);
          border: 1px solid rgba(99, 102, 241, 0.4);
          color: #a5b4fc;
          padding: 6px 14px;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        }
        .admin-refresh:hover { background: rgba(99, 102, 241, 0.3); }
        .admin-refresh:disabled { opacity: 0.5; cursor: not-allowed; }
        .admin-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          margin-bottom: 20px;
        }
        .admin-tab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          background: transparent;
          border: none;
          color: var(--text-secondary, #AAA);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: color 0.15s;
        }
        .admin-tab:hover { color: var(--text-primary, #FFF); }
        .admin-tab.active {
          color: #a5b4fc;
          border-bottom-color: #6366f1;
        }
        .admin-content {
          max-width: 1100px;
        }
        .admin-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 60px;
          color: var(--text-secondary, #AAA);
        }
        .admin-stat-cards {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }
        .stat-card {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 20px 28px;
          text-align: center;
          min-width: 120px;
        }
        .stat-card.action {
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #a5b4fc;
        }
        .stat-card.action:hover { background: rgba(99, 102, 241, 0.1); }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #a5b4fc;
        }
        .stat-label {
          font-size: 12px;
          color: var(--text-secondary, #AAA);
          margin-top: 4px;
        }
        .admin-section-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-secondary, #CCC);
          margin: 0 0 12px;
        }
        .admin-table-wrap {
          overflow-x: auto;
        }
        .admin-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }
        .admin-table th {
          text-align: left;
          padding: 10px 14px;
          background: rgba(255,255,255,0.04);
          color: var(--text-secondary, #AAA);
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .admin-table td {
          padding: 10px 14px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          color: var(--text-primary, #EEE);
        }
        .admin-table tr:hover td {
          background: rgba(255,255,255,0.02);
        }
        .admin-empty {
          text-align: center;
          color: var(--text-secondary, #888);
          padding: 32px;
        }
        .role-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }
        .role-badge.student {
          background: rgba(16, 185, 129, 0.15);
          color: #34d399;
        }
        .role-badge.instructor {
          background: rgba(99, 102, 241, 0.15);
          color: #a5b4fc;
        }
        .role-badge.admin {
          background: rgba(245, 158, 11, 0.15);
          color: #fbbf24;
        }
        .progress-bar-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .progress-bar-wrap span {
          font-size: 12px;
          color: var(--text-secondary, #AAA);
          min-width: 36px;
        }
        .progress-bar {
          height: 6px;
          background: #6366f1;
          border-radius: 3px;
          flex: 1;
          max-width: 120px;
          transition: width 0.3s;
        }
        .score-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }
        .score-badge.high {
          background: rgba(16, 185, 129, 0.15);
          color: #34d399;
        }
        .score-badge.mid {
          background: rgba(59, 130, 246, 0.15);
          color: #93c5fd;
        }
        .score-badge.low {
          background: rgba(239, 68, 68, 0.15);
          color: #fca5a5;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
